angular.module('zc.masterApi')

.factory 'ZcsKpi', ($q, MasterAccessStats, MasterCampaignStats) ->
  class ZcsKpi

    INSTALLATION_STEPS = [
      'siteinfo_confirmed'
      'conversion_tag_confirmed'
      'first_campaign_start'
    ]

    @firstCampaignStartAt: (campaigns) ->
      start_ats = (moment(campaign.start_at) for campaign in campaigns)
      if start_ats.length then moment.min(start_ats) else null

    @lastCampaignEndAt: (campaigns) ->
      end_ats = (moment(campaign.end_at) for campaign in campaigns)
      if end_ats.length then moment.max(end_ats) else null

    constructor: (projects, start_at) ->
      @start_at = moment(start_at)
      @end_at = moment(@start_at).add(1, 'month')
      @projects = _.filter projects, (project) => @isCurrentOrBefore(project.created_at)
      @registered_projects = _.filter projects, (project) => @isCurrent(project.created_at)
      @campaign_started_projects = _.filter projects, (project) => @getBeforeCampaigns(project).length
      @campaign_current_projects = _.filter projects, (project) => @getCurrentCampaigns(project).length
      @campaign_setting_projects = _.filter @campaign_started_projects, (project) => @getCurrentCampaigns(project).length

    calculate: ->
      $q.all [
        @getProjectPerformance()
        @getCampaignPerformance()
      ]
      .then ([project_performance, campaign_performance]) =>
        projects: @projects
        registered_projects: @registered_projects
        corrected_registered_projects_count: @getCorrectedRegisteredProjectsCount()
        campaign_started_projects: @campaign_started_projects
        campaign_current_projects: @campaign_current_projects
        campaign_setting_projects: @campaign_setting_projects
        project_performance: project_performance
        installation_completion_rates: @getInstallationProceededRates()
        campaign_delivery_rates: @getCampaignDeliveryRates()
        campaign_performance: campaign_performance

    getInstallationStepCounts: ->
      counts = {}
      for step in INSTALLATION_STEPS
        counts[step] = 0
      for project in @registered_projects
        project.first_campaign_start_at = ZcsKpi.firstCampaignStartAt(project.started_campaigns)
        for step in INSTALLATION_STEPS
          counts[step] += 1 if @isCurrent(project["#{step}_at"])
      counts

    getInstallationProceededRates: ->
      counts = @getInstallationStepCounts()
      installation_completion_rates = {}
      for step, i in INSTALLATION_STEPS
        installation_completion_rates[step] = if @registered_projects.length then counts[step] / @registered_projects.length else null
      installation_completion_rates

    getCampaignDeliveryRates: ->
      setting_count = 0
      delivery_term = 0
      for project in @campaign_started_projects
        campaigns = @getCurrentCampaigns(project)
        setting_count += 1 if campaigns.length
        first_campaign_start_at = ZcsKpi.firstCampaignStartAt(campaigns)
        last_campaign_end_at = ZcsKpi.lastCampaignEndAt(campaigns)
        if first_campaign_start_at and last_campaign_end_at
          term_start_at = moment.max(@start_at, first_campaign_start_at)
          term_end_at = moment.min(@end_at, last_campaign_end_at)
          delivery_term += term_end_at.diff(term_start_at)
      setting = if @campaign_started_projects.length then setting_count / @campaign_started_projects.length else null
      total_term = @end_at.diff(@start_at) * @campaign_started_projects.length
      term = if total_term then delivery_term / total_term else null
      {setting, term}

    getProjectPerformance: ->
      @getAccessStats(@projects)
      .then (access_stats) ->
        conversion_price: access_stats.conversion_price

    getCampaignPerformance: ->
      $q.all [
        @getAccessStats(@campaign_current_projects)
        @getCampaignStats(@campaign_current_projects)
      ]
      .then ([access_stats, campaign_stats]) ->
        session_cover_rate: calculateSessionCoverRate(access_stats.session_count, campaign_stats.experimental.delivery_count)
        conversion_cover_rate: calculateConversionCoverRate(access_stats.conversion_price, campaign_stats.experimental.conversion_price)
        conversion_up_rate: campaign_stats.conversion_up_rate
        campaign_coupon_used_rate: campaign_stats.experimental.campaign_coupon_used_rate

    isCurrent: (date) ->
      moment(date).isSame(@start_at) or moment(date).isBetween(@start_at, @end_at)

    isCurrentOrBefore: (date) ->
      moment(date).isBefore(@end_at)

    isCurrentOrAfter: (date) ->
      moment(date).isAfter(@start_at)

    getCurrentCampaigns: (project) ->
      _.filter project.started_campaigns, (campaign) =>
         @isCurrentOrBefore(campaign.start_at) and @isCurrentOrAfter(campaign.end_at)

    getBeforeCampaigns: (project) ->
      _.filter project.started_campaigns, (campaign) =>
        @isCurrentOrBefore(campaign.start_at)

    getCampaignIds: (projects) ->
      _.chain projects
       .reduce ((campaigns, project) => campaigns.concat(@getCurrentCampaigns(project))), []
       .map (campaign) -> campaign.id
       .value()

    getCampaignStats: (projects) ->
      new MasterCampaignStats('daily_all').aggregate
        campaign_ids: @getCampaignIds(projects)
        start_at: @start_at.valueOf()
        end_at: @end_at.valueOf()
      .then (res) ->
        # Note: MasterCamapignStats does not support FacadeV2 yet so this is very ad-hoc
        campaign_stats = MasterCampaignStats.statsById(res)['undefined']
        MasterCampaignStats.calculate campaign_stats
        campaign_stats

    getAccessStats: (projects) ->
      apikeys = _.map(projects, (project) -> project.apikey)
      apikeyChunks = _.chunk(apikeys, 50)
      # request too long so split in chunks
      promises = _.map apikeyChunks, (apikeyChunk) =>
        MasterAccessStats.aggregate
          dimension: 'daily'
        ,
          match:
            apikey:
              $in: apikeyChunk
            start_at:
              $gte: @start_at.toDate()
              $lt: @end_at.toDate()
            device: null # total for site
            is_control: null # total for site
            campaign_segment_id: null
          pipeline: [
            $group:
              _id: null
              session_count:
                $sum: '$session_count'
              conversion_price:
                $sum: '$conversion_price'
          ]
        .then (res) ->
          return {session_count: 0, conversion_price: 0} unless res.length
          res[0]

      $q.all(promises)
      .then (results) ->
        reduced = _.reduce(results, (chunk, obj) ->
          obj.conversion_price += chunk.conversion_price
          obj.session_count += chunk.session_count
          obj
        , { conversion_price: 0, session_count: 0})
        reduced

    getCorrectedRegisteredProjectsCount: ->
      @registered_projects.length * @calculateScale()

    calculateScale: ->
      total_hours = @end_at.diff(@start_at, 'hours')
      current_hours = moment.min(@end_at, moment()).diff(@start_at, 'hours')
      return 0 if current_hours is 0
      total_hours / current_hours

    calculateSessionCoverRate = (session_count, delivery_count) ->
      if session_count then delivery_count / session_count else null

    calculateConversionCoverRate = (conversion_price, campaign_conversion_price) ->
      if conversion_price then campaign_conversion_price / conversion_price else null



// WEBPACK FOOTER //
// ./src/master_admin_modules/master_api/zcs_kpi.service.coffee